import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellIcon, BellOffIcon, CalendarPlusIcon, CheckIcon, ClockIcon, MailIcon, MessageCircleIcon, PhoneIcon, UserIcon, VideoIcon, XIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { DeleteButton } from '../ui/DeleteButton';
import { ProductImage } from '../../ui/ProductImage';
import { useAdmin } from '../AdminContext';
import { whatsappNumber } from '../customers/customerRules';
import { btnGhost, btnPrimary, btnSecondary } from '../ui/styles';
import { TimeChoice } from './TimeChoice';
import { CompleteForm } from './CompleteForm';
import { PerformerRow } from './PerformerRow';
import { useCan } from '../useCan';
import { firstName, stateOf } from './agendaRules';
import { REMINDER_HOURS, endTime, hasStarted, isActive, reminderState } from '../../core/rules/agenda';
import { useNow } from '../../hooks/useNow';
import { telHref } from '../../core/rules/contact';
import { byStart, dayLong, dayMedium, timeOf, today } from '../../core/format/dates';
import { FollowUpForm } from './FollowUpForm';
import { staffList } from '../../core/rules/salonDays';
import { bookingProblem, courseWarning, requestProblem } from '../../core/rules/staffPermissions';
import { ADMIN_BASE } from '../../utils/hosts';
import type { AgendaAppointment, AgendaBlock } from '../../core/types/agenda';
import type { AgendaControl } from './useAgenda';
type Props = {
  appointment: AgendaAppointment;
  /** Alle afspraken van de geladen week, voor vrije tijden en "vorige keer". */
  appointments: AgendaAppointment[];
  blocks: AgendaBlock[];
  agenda: AgendaControl;
  onClose: () => void;
};
type Mode = 'view' | 'move' | 'complete' | 'followup';

/** Zelfde klant: op id, of (bij gasten) op een ingevuld e-mailadres. Twee lege adressen zijn niet dezelfde. */
const sameCustomer = (x: AgendaAppointment['customer'], y: AgendaAppointment['customer']) => x.id || y.id ? x.id === y.id : Boolean(x.email) && x.email.toLowerCase() === y.email.toLowerCase();
const reminderText = {
  verstuurd: 'Herinnering verstuurd',
  gepland: `Herinnering gaat ${REMINDER_HOURS} uur vooraf`,
  'te-kort': 'Geen herinnering: binnen een dag geboekt',
  geen: ''
} as const;

/** Eén afspraak: wie, wat, wanneer, en wat Sarah ermee kan. De acties volgen de dag. */
export function AppointmentDetail({
  appointment: a,
  appointments,
  blocks,
  agenda,
  onClose
}: Props) {
  const {
    index,
    site
  } = useAdmin();
  const can = useCan();
  // Plannen voor de hele salon (Sarah) of alleen op eigen dagen (collega): bookingProblem/requestProblem.
  const viewer = can.viewer('agenda.plan');
  const navigate = useNavigate();
  const team = staffList(site.staff);
  const rules = {
    hours: site.openingHours,
    exceptions: site.salonExceptions,
    staff: team
  };
  const [mode, setMode] = useState<Mode>('view');
  const [date, setDate] = useState(a.date);
  const [time, setTime] = useState(a.time);
  const [overlap, setOverlap] = useState(false);
  const [confirmNoShow, setConfirmNoShow] = useState(false);
  const busy = agenda.isBusy(a.id);
  const treatment = index.treatmentBySlug(a.treatmentSlug);
  const state = stateOf(a);
  const open = isActive(a);
  // Elke minuut opnieuw: "Afronden" verschijnt op de begintijd, niet pas na een andere klik.
  const now = useNow();
  const started = hasStarted(a, now);
  const reminder = open ? reminderState(a, now) : 'geen';
  const wa = a.customer.phone ? whatsappNumber(a.customer.phone) : '';

  // De vorige afgeronde behandeling van deze klant in de geladen week(en): wat ze toen gebruikte.
  const previous = appointments.filter(x => x.id !== a.id && x.outcome === 'afgerond' && sameCustomer(x.customer, a.customer) && byStart(x, a) < 0).sort((x, y) => byStart(y, x))[0];
  const name = (id: string) => index.productById(id)?.name ?? id;
  // Collega: aanvragen alleen op haar eigen dag; verzetten alleen naar een eigen dag.
  const requestBlocked = a.status === 'aanvraag' ? requestProblem(viewer, a, rules) : null;
  const moveProblem = mode === 'move' && date ? bookingProblem(viewer, {
    date,
    treatmentSlug: a.treatmentSlug
  }, rules) : null;
  // Kuursessie bij een ander dan bij wie de kuur loopt: alleen een waarschuwing.
  const kuurWarning = a.session && open ? courseWarning(a.customer.kuur?.staffId, a.staffId, team) : '';

  // Escape sluit normaal het paneel (Agenda.tsx). Tijdens afronden niet: dan gaat het ingevulde verloren.
  useEffect(() => {
    if (mode !== 'complete') return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && e.preventDefault();
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [mode]);
  return <div className="space-y-5" data-id="element-253" data-mp="37pfp8e9ia8sc">
      <div className="flex items-start justify-between gap-3" data-id="element-254" data-mp="37rada1g2qh0n">
        <div className="min-w-0" data-id="element-255" data-mp="3lp77wsaywdco">
          <div className="flex flex-wrap items-center gap-1.5" data-id="element-256" data-mp="3lqi9qmkurb15">
            <Badge tone={state.tone} data-id="element-257" data-mp="3gr5pm9ld8i29">{state.label}</Badge>
            {a.customer.firstVisit && open && <Badge tone="soft" data-id="element-258" data-mp="47b6wb7gc30b">Eerste bezoek</Badge>}
            {a.session && <Badge tone="neutral" data-id="element-259" data-mp="44ym7qab7ejk">Kuursessie {a.session}</Badge>}
          </div>
          <h2 className="mt-2 font-display text-[1.5rem] font-light leading-tight text-ink" data-id="element-260" data-mp="95mfkim0g7gy">{a.customer.name}</h2>
          <p className="mt-0.5 text-[14px] text-ink" data-id="element-261" data-mp="3rdtzdun2ixma">{treatment?.name ?? a.treatmentSlug}</p>
        </div>
        {/* Tijdens afronden geen kruisje: één misklik zou ingevulde producten en advies weggooien. "Terug" staat in het formulier. */}
        {mode !== 'complete' && <button type="button" onClick={onClose} className={btnGhost} aria-label="Sluiten" data-id="element-262" data-mp="3me8yvfs92h7g">
            <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-263" data-mp="37p301sxbqt03" />
          </button>}
      </div>

      <dl className="space-y-2 text-[14px]" data-id="element-264" data-mp="3cn4jieynwuc3">
        <div className="flex items-center gap-2.5" data-id="element-265" data-mp="2ymk1vm4hh4k6">
          <dt className="sr-only" data-id="element-266" data-mp="2ynvembfwpmnf">Wanneer</dt>
          <ClockIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-267" data-mp="33mojxgl1i7ci" />
          <dd className="text-ink" data-id="element-268" data-mp="196ht565tg4hg">
            <span className="capitalize" data-id="element-269" data-mp="197j47amu0qz6">{dayLong(a.date)}</span>, {a.time}–{endTime(a)}
            <span className="text-muted" data-id="element-270" data-mp="14eg8avul15ft"> · {a.durationMinutes} min</span>
          </dd>
        </div>
        {a.kind === 'video' && <div className="flex items-center gap-2.5" data-id="element-271" data-mp="ze26hvjkqrq8">
            <dt className="sr-only" data-id="element-272" data-mp="vfl05e7z9l9v">Soort</dt>
            <VideoIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-273" data-mp="vcqg3nsvm2gz" />
            <dd className="text-ink" data-id="element-274" data-mp="1s2g8emdsa872">Videoconsult</dd>
          </div>}
        <PerformerRow appointment={a} agenda={agenda} canChange={can.canAll('agenda.plan') && open} data-id="element-275" data-mp="1n6iqibmvgts6" />
        {kuurWarning && <p className="rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-276" data-mp="1j5yxnk9c0cbu">{kuurWarning}</p>}
        {a.handledBy && team.length > 1 && <p className="text-[13px] text-muted" data-id="element-277" data-mp="7p8wifjrmap3">
            {a.handledBy.action === 'bevestigd' ? 'Bevestigd' : 'Andere tijd voorgesteld'} door {a.handledBy.name}
          </p>}
        {a.arrivedAt && open && <p className="text-[13px] text-muted" data-id="element-278" data-mp="2eeajm6xm7doj">Binnen sinds {timeOf(a.arrivedAt)}</p>}
        {reminder !== 'geen' && <div className="flex items-center gap-2.5" data-id="element-279" data-mp="2jcu5k7c7kpgw">
            <dt className="sr-only" data-id="element-280" data-mp="2nde5tjs2w979">Herinnering</dt>
            {reminder === 'te-kort' ? <BellOffIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-281" data-mp="2s4e3wscxoxbc" /> : <BellIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-282" data-mp="2x2nyb0qu3tvw" />}
            <dd className="text-muted" data-id="element-283" data-mp="3218xcwuco7ir">{reminderText[reminder]}</dd>
          </div>}
      </dl>

      {/* Contact: bellen als ze te laat is, appen om te verzetten. */}
      <div className="flex flex-wrap gap-1.5" data-id="element-284" data-mp="3b3rwb5v0lzzq">
        {a.customer.phone && <a href={telHref(a.customer.phone)} className={btnSecondary} data-id="element-285" data-mp="1coduqjro5pwy">
            <PhoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-286" data-mp="1cq8is6y8ly59" />
            Bellen
          </a>}
        {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className={btnSecondary} data-id="element-287" data-mp="xsfhtdfaesdg">
            <MessageCircleIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-288" data-mp="st2teo8rpd8r" />
            WhatsApp
          </a>}
        {a.customer.email && <a href={`mailto:${a.customer.email}`} className={btnSecondary} data-id="element-289" data-mp="12jxjk3p4wwzh">
            <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-290" data-mp="12m1rqxu2gi5k" />
            Mail
          </a>}
        {a.customer.id && <Link to={`${ADMIN_BASE}/klanten/${a.customer.id}`} className={btnSecondary} data-id="element-291" data-mp="9w1iynwg2kxf">
            <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-292" data-mp="nvkkgxur2y0r" />
            Dossier
          </Link>}
      </div>

      {/* Afrekenen in de kassa: na afronden (of al tijdens de behandeling), zolang er geen bon is. */}
      {a.kind === 'salon' && a.status === 'bevestigd' && a.outcome !== 'geannuleerd' && a.outcome !== 'no-show' && (a.outcome === 'afgerond' || started) && <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4 text-[13px]" data-id="element-293" data-mp="22rvtnuusayzr">
          {a.saleId ? <span className="text-muted" data-id="element-294" data-mp="26uihlkoc3zro">Afgerekend in de kassa.</span> : <Link to={`${ADMIN_BASE}/kassa?afspraak=${a.id}`} className={a.outcome === 'afgerond' ? btnPrimary : btnSecondary} data-id="element-295" data-mp="1suzifhdi3yri">
              Afrekenen
            </Link>}
          {a.outcome === 'afgerond' && mode === 'view' && <button type="button" onClick={() => setMode('followup')} className={btnSecondary} data-id="element-296" data-mp="2lkzecqq0y1i8">
              <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-297" data-mp="2qjswiwg9agoq" />
              Plan vervolg
            </button>}
        </div>}

      {/* Na afloop: wat er gebruikt en meegegeven is. */}
      {a.outcome === 'afgerond' && (a.products?.used.length || a.products?.advice.length || a.treatmentNote) && <section className="space-y-3 border-t border-line pt-4" data-id="element-298" data-mp="398h8uu0obdow">
          {a.products && a.products.used.length > 0 && <p className="text-[13px] text-muted" data-id="element-299" data-mp="28c6bzjm6yfh1">
              <span className="text-ink" data-id="element-300" data-mp="2dahjvosxefwm">Gebruikt:</span> {a.products.used.map(name).join(', ')}
            </p>}
          {a.products && a.products.advice.length > 0 && <div data-id="element-301" data-mp="24a1jio7hbovz">
              <p className="text-[13px] text-ink" data-id="element-302" data-mp="249jqs1k0zsst">Advies voor thuis</p>
              <ul className="mt-2 space-y-1.5" data-id="element-303" data-mp="24bw584qkrl14">
                {a.products.advice.map(id => {
            const p = index.productById(id);
            return <li key={id} className="flex items-center gap-2.5 text-[13px]" data-id="element-304" data-mp="1ud6le6f4bqi8">
                      {p && <ProductImage src={p.image} className="h-8 w-8 shrink-0 rounded bg-sand object-contain p-0.5" data-id="element-305" data-mp="1gm1vvx5gselm" />}
                      <span className="text-ink" data-id="element-306" data-mp="1kjzlh135d2pj">{name(id)}</span>
                    </li>;
          })}
              </ul>
              <p className="mt-2 text-[12px] text-muted" data-id="element-307" data-mp="1blxem4wqffuc">{a.customer.id ? 'Staat bij haar bezoek in Mijn omgeving, met een link om te bestellen.' : 'Meegestuurd in de mail na de behandeling.'}</p>
            </div>}
          {a.treatmentNote && <p className="whitespace-pre-line rounded-md bg-sand px-3 py-2.5 text-[13px] text-ink" data-id="element-308" data-mp="n2c1jixq5qcj">{a.treatmentNote}</p>}
        </section>}

      {open && mode === 'view' && <section className="space-y-3 border-t border-line pt-4" data-id="element-309" data-mp="wxvz09rxsx3e">
          {previous?.products && (previous.products.advice.length > 0 || previous.treatmentNote) && <div className="rounded-md bg-sand px-3 py-2.5 text-[13px]" data-id="element-310" data-mp="15zwqeq0hoox5">
              <p className="text-muted" data-id="element-311" data-mp="1ko9q9y2p6rwx">Vorige keer ({dayMedium(previous.date)})</p>
              {previous.products.advice.length > 0 && <p className="mt-0.5 text-ink" data-id="element-312" data-mp="1fon5n3kji9e3">Advies: {previous.products.advice.map(name).join(', ')}</p>}
              {previous.treatmentNote && <p className="mt-0.5 text-ink" data-id="element-313" data-mp="1tphchhrxw2an">{previous.treatmentNote}</p>}
            </div>}

          {requestBlocked && <p className="text-[13px] text-muted" data-id="element-314" data-mp="1pl9nrklhemcf">{requestBlocked} Sarah handelt deze aanvraag af.</p>}
          <div className="flex flex-wrap gap-2" data-id="element-315" data-mp="1wog7ccodn8md">
            {a.status === 'aanvraag' && !requestBlocked && <button type="button" onClick={() => void agenda.confirm(a.id)} disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-316" data-mp="26oqjrv2l8c0h">
                <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-317" data-mp="2bfp4r7yitya2" />
                Bevestigen
              </button>}
            {a.status === 'bevestigd' && !started && !a.arrivedAt && a.date === today() && <button type="button" onClick={() => void agenda.arrive(a.id)} disabled={busy} className={btnSecondary} data-id="element-318" data-mp="2ki83bfv9xoqi">
                Binnen
              </button>}
            {a.status === 'bevestigd' && started && <button type="button" onClick={() => setMode('complete')} disabled={busy} className={btnPrimary} data-id="element-319" data-mp="v1gtwan45de9">
                <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-320" data-mp="zzi9ump8swli" />
                Afronden
              </button>}
            {!requestBlocked && <button type="button" onClick={() => setMode('move')} disabled={busy} className={btnSecondary} data-id="element-321" data-mp="38k3ajjikhnox">
                {a.status === 'aanvraag' ? 'Andere tijd voorstellen' : 'Verzetten'}
              </button>}
            {a.status === 'bevestigd' && a.customer.email && <button type="button" onClick={() => setMode('followup')} disabled={busy} className={btnGhost} data-id="element-322" data-mp="3rpzlvc0tpe">
                <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-323" data-mp="3s7as1q70b978" />
                Plan vervolg
              </button>}
            {started && !confirmNoShow && <button type="button" onClick={() => setConfirmNoShow(true)} disabled={busy} className={btnGhost} data-id="element-324" data-mp="3ia653o5c79pd">
                Niet gekomen
              </button>}
          </div>
          {/* Niet gekomen sluit de afspraak af en is niet terug te draaien: één bevestiging, met wat het betekent. */}
          {confirmNoShow && <div className="rounded-md bg-sand px-3 py-3 text-[13px]" role="alertdialog" aria-label="Niet gekomen bevestigen" data-id="element-325" data-mp="3upw2p3obw6l6">
              <p className="text-ink" data-id="element-326" data-mp="3lqhds8nt842s">
                Afspraak afsluiten als niet gekomen?{' '}
                {a.session ? site.policy.noShowUsesSession ? `Sessie ${a.session} van de kuur telt als gebruikt (no-show-regel staat aan).` : 'De kuursessie telt niet en het nummer komt vrij.' : ''}{' '}
                {firstName(a.customer.name)} krijgt geen bericht.
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-327" data-mp="8b2tn63d5w2o">
                <button type="button" disabled={busy} aria-busy={busy} onClick={async () => {
            await agenda.noShow(a.id);
            setConfirmNoShow(false);
          }} className={btnSecondary} data-id="element-328" data-mp="da5zgsr646bp">
                  {busy ? 'Bezig…' : 'Ja, niet gekomen'}
                </button>
                <button type="button" disabled={busy} onClick={() => setConfirmNoShow(false)} className={btnGhost} data-id="element-329" data-mp="2lh9bua1whq4b">
                  Terug
                </button>
              </div>
            </div>}
          <DeleteButton label="Annuleren" warning={`${firstName(a.customer.name)} krijgt bericht`} onConfirm={() => void agenda.cancel(a.id)} data-id="element-330" data-mp="2ujiiupxyn1cy" />
        </section>}

      {open && mode === 'move' && <section className="space-y-4 border-t border-line pt-4" data-id="element-331" data-mp="22tnt4s66x9nr">
          <TimeChoice date={date} onDate={setDate} time={time} onTime={setTime} durationMinutes={a.durationMinutes} appointments={appointments} blocks={blocks} ignoreId={a.id} overlap={overlap} onOverlap={setOverlap} data-id="element-332" data-mp="2gjhkpi92y816" />
          {moveProblem && <p role="alert" className="text-[13px] text-clay" data-id="element-333" data-mp="3j6d05lzpyy6l">
              {moveProblem}
            </p>}
          <div className="flex flex-wrap gap-2" data-id="element-334" data-mp="3o4eexuq44bc9">
            <button type="button" disabled={busy || !time || date === a.date && time === a.time || Boolean(moveProblem)} aria-busy={busy} onClick={async () => {
          const moved = await agenda.move(a.id, {
            date,
            time,
            ...(overlap ? {
              allowOverlap: true
            } : {})
          });
          if (moved) setMode('view');
        }} className={btnPrimary} data-id="element-335" data-mp="3o33f25zsfnqf">
              {time ? `Verzet naar ${dayMedium(date)} ${time}` : 'Kies een tijd'}
            </button>
            <button type="button" onClick={() => setMode('view')} className={btnGhost} data-id="element-336" data-mp="3e78moj5rieag">
              Laat staan
            </button>
          </div>
          <p className="text-[12px] text-muted" data-id="element-337" data-mp="ph8ta537atra">{firstName(a.customer.name)} krijgt bericht met de nieuwe tijd; de uitnodiging in haar agenda schuift mee.</p>
        </section>}

      {open && mode === 'complete' && <section className="border-t border-line pt-4" data-id="element-338" data-mp="29xgpjmiub6sh">
          <CompleteForm appointment={a} previousUsed={previous?.products?.used ?? []} busy={busy} onCancel={() => setMode('view')} onSubmit={async req => {
        const done = await agenda.complete(a.id, req);
        if (!done) return;
        setMode('view');
        // Afronden → afrekenen: meteen de bon, met de behandeling (uit kuur als het kan) en het advies.
        if (done.kind === 'salon' && !done.saleId) navigate(`${ADMIN_BASE}/kassa?afspraak=${done.id}`);
      }} data-id="element-339" data-mp="29y07v4cejx71" />
        </section>}

      {mode === 'followup' && <section className="border-t border-line pt-4" data-id="element-340" data-mp="3gldituhn5jyx">
          <FollowUpForm appointment={a} appointments={appointments} blocks={blocks} agenda={agenda} onCancel={() => setMode('view')} onDone={() => setMode('view')} data-id="element-341" data-mp="3gkvpb5md5jup" />
        </section>}
    </div>;
}